import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usersAPI } from '@/services/api/users.api';
import { Spinner } from '@/components/atoms/Spinner';
import { buildRoute, ROUTES } from '@/constants/routes';
import { useSavedVocabulary } from '@/context/SavedVocabularyContext';
import { getWeekStartMs } from '../utils/wordPeriodRanges';
import styles from './LearningStatsSection.module.scss';

export function LearningStatsSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { savedItems, isLoading: savedLoading } = useSavedVocabulary();
  const [wordsThisWeek, setWordsThisWeek] = useState<number | null>(null);
  const [learnedLoading, setLearnedLoading] = useState(true);

  /** Card: solo cuenta lo guardado en la semana actual (paralelo a "Aprendido esta semana"). */
  const savedVocabWeekCount = useMemo(() => {
    const weekStart = getWeekStartMs();
    return savedItems.filter((s) => s.savedAt >= weekStart).length;
  }, [savedItems]);

  useEffect(() => {
    const now = Date.now();
    const weekStart = getWeekStartMs();

    // Mismo filtro que LearnedWordsListPage: solo palabras genuinamente
    // nuevas esta semana (excluye repetidas de semanas anteriores) y
    // deduplica por LOWER(TRIM(word)) en backend para no contar dos veces
    // la misma palabra cuando vino de dos fuentes (historia + topic).
    void usersAPI
      .getLearnedWordsCount({ from: weekStart, to: now, excludeSeenBefore: weekStart })
      .then(({ count }) => setWordsThisWeek(count))
      .catch(() => setWordsThisWeek(0))
      .finally(() => setLearnedLoading(false));
  }, []);

  const loading = learnedLoading || savedLoading;
  // No ocultamos la sección aunque el alumno aún no tenga palabras
  // (caso de usuario nuevo): preferimos mostrar las cards con 0 para
  // que sepan que la feature existe.

  return (
    <section className={styles.learningStats} aria-labelledby="progress-vocab-heading">
      <div className={styles.learningStats__message}>
        <h2 id="progress-vocab-heading" className={styles.learningStats__title}>
          {t('learningStats.title')}
        </h2>
      </div>
      {loading ? (
        <div className={styles.learningStats__loading}>
          <Spinner size="md" aria-label={t('common.loading')} />
        </div>
      ) : (
        <div className={styles.learningStats__stats}>
          <button
            type="button"
            className={styles.learningStats__statItem}
            data-no-press-scale=""
            onClick={() =>
              navigate(buildRoute(ROUTES.STUDENT.PROGRESS_LEARNED_WORDS, { period: 'week' }))
            }
            aria-label={t('learningStats.vocabCardWeekAria')}
          >
            <span className={styles.learningStats__statValue}>{wordsThisWeek ?? 0}</span>
            <span className={styles.learningStats__statLabel}>{t('learningStats.vocabCardWeekLabel')}</span>
          </button>
          <button
            type="button"
            className={styles.learningStats__statItem}
            data-no-press-scale=""
            onClick={() =>
              navigate(buildRoute(ROUTES.STUDENT.PROGRESS_LEARNED_WORDS, { period: 'week' }), {
                state: { learnedWordsSegment: 'saved' as const },
              })
            }
            aria-label={t('learningStats.vocabCardSavedAria')}
          >
            <span className={styles.learningStats__statValue}>{savedVocabWeekCount}</span>
            <span className={styles.learningStats__statLabel}>{t('learningStats.vocabCardSavedLabel')}</span>
          </button>
        </div>
      )}
    </section>
  );
}
